import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { Alert, Button, Input } from '@/components/ui';
import { useAuth } from '@/hooks/useAuth';
import { applyFieldErrors, errorMessage } from '@/hooks/useApiError';
import { dashboardFor, paths } from '@/routes/paths';
import { ApiError } from '@/types/api';
import { cn } from '@/utils/cn';
import { loginSchema } from '@/validations';

import { AuthCard } from './AuthCard';

const ACCOUNT_TYPES = [
  { key: 'student', label: 'Student' },
  { key: 'company', label: 'Company' },
];

/**
 * One sign-in form, two doors.
 *
 * /login is for students and companies, with a tab for each; `portal="admin"`
 * (/admin/login) is for platform administrators. The tab or the admin portal is
 * sent with the credentials and the API enforces it: the admin page turns away
 * everyone else exactly as a wrong password would, each tab refuses the other
 * kind of account and names the right tab, and either tab answers an
 * administrator with `ADMIN_SIGN_IN_REQUIRED`, which is shown as a link to the
 * right page.
 */
export function LoginPage({ portal = 'user' }) {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = portal === 'admin';
  const [accountType, setAccountType] = useState('student');

  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: zodResolver(loginSchema) });

  const from = location.state?.from?.pathname;

  function chooseAccountType(type) {
    setAccountType(type);
    clearErrors('root');
  }

  async function onSubmit(values) {
    try {
      const session = await login({ ...values, portal: isAdmin ? portal : accountType });
      if (!session.user.is_email_verified) {
        // Signing in to an unverified account emails a fresh code.
        navigate(`${paths.verifyEmail}?email=${encodeURIComponent(session.user.email)}`, {
          state: { codeSent: true },
        });
        return;
      }
      // A page that sent them here to sign in wins; otherwise their dashboard.
      // The marketing home is never a destination after signing in.
      const destination =
        from && from !== paths.home
          ? from
          : isAdmin
            ? paths.admin.overview
            : dashboardFor(session.user.role);
      navigate(destination, { replace: true });
    } catch (error) {
      if (!applyFieldErrors(error, setError)) {
        setError('root', { message: errorMessage(error, 'Could not sign in') });
      }
      // A blocked account is a policy decision, not a bad password: say so.
      if (error instanceof ApiError && error.status === 403) {
        setError('root', {
          type: error.code === 'ADMIN_SIGN_IN_REQUIRED' ? 'admin' : 'forbidden',
          message: error.message,
        });
      }
    }
  }

  return (
    <AuthCard
      title={isAdmin ? 'Admin sign in' : 'Log In'}
      description={isAdmin ? 'For platform administrators only.' : undefined}
      footer={
        <>
          {/* Administrators are created by another admin, never signed up. */}
          {!isAdmin && (
            <p>
              Don&apos;t have an account?{' '}
              <Link
                to={accountType === 'company' ? paths.registerCompany : paths.registerStudent}
                className="text-brand-600 font-medium"
              >
                Sign up
              </Link>
            </p>
          )}
          <p>
            <Link to={paths.forgotPassword} className="text-ink underline">
              Forgot Password?
            </Link>
          </p>
        </>
      }
    >
      {!isAdmin && <AccountTabs value={accountType} onChange={chooseAccountType} />}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        {errors.root && (
          <Alert tone="error">
            {errors.root.message}
            {errors.root.type === 'admin' && (
              <>
                {' '}
                <Link to={paths.admin.login} className="font-semibold underline">
                  Go to admin sign in
                </Link>
              </>
            )}
          </Alert>
        )}

        <Input
          label="Email"
          type="email"
          autoComplete="email"
          required
          error={errors.email?.message}
          {...register('email')}
        />
        <Input
          label="Password"
          type="password"
          autoComplete="current-password"
          required
          error={errors.password?.message}
          {...register('password')}
        />

        <Button type="submit" fullWidth isLoading={isSubmitting}>
          {isAdmin ? 'Sign in' : 'Log In'}
        </Button>
      </form>

      {!isAdmin && (
        <p className="text-ink-muted mt-6 flex items-center gap-3 text-xs">
          <span aria-hidden="true" className="bg-line h-px flex-1" />
          OR
          <span aria-hidden="true" className="bg-line h-px flex-1" />
        </p>
      )}
    </AuthCard>
  );
}

/**
 * Student or company. Decides what the login is sent as, which the API checks
 * against the account, and which registration "Sign up" opens.
 */
function AccountTabs({ value, onChange }) {
  return (
    <div
      role="group"
      aria-label="Account type"
      className="bg-surface-muted mb-5 grid grid-cols-2 gap-1 rounded-lg p-1"
    >
      {ACCOUNT_TYPES.map((type) => (
        <button
          key={type.key}
          type="button"
          aria-pressed={type.key === value}
          onClick={() => onChange(type.key)}
          className={cn(
            'rounded-md px-3 py-2 text-sm font-medium transition',
            type.key === value
              ? 'bg-surface text-brand-700 shadow-sm'
              : 'text-ink-muted hover:text-ink',
          )}
        >
          {type.label}
        </button>
      ))}
    </div>
  );
}
